import React, { useState, useEffect } from 'react';
import { DiseaseGroup } from '../../types/diseases';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface CreateDiseaseGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string }) => Promise<void>;
  initialData?: DiseaseGroup;
}

export function CreateDiseaseGroupModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: CreateDiseaseGroupModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
    } else {
      setName('');
      setDescription('');
    }
    setError('');
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit({ name: name.trim(), description: description.trim() });
      setName('');
      setDescription('');
      onClose();
    } catch (err) {
      setError('Failed to save disease group');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-md">
      <BrandModalHeader
        title={initialData ? 'Edit Disease Group' : 'Create Disease Group'}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-700"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Enter disease group name"
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-slate-700"
            >
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Enter description"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {isSubmitting
              ? 'Saving...'
              : initialData
              ? 'Update'
              : 'Create'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
}
